// components/AuthForm.tsx
// Form component for user authentication (sign up/sign in)
'use client'

import React from 'react'
import PasswordInput from '@/components/PasswordInput'
import { UserRole } from '@/types'

interface AuthFormProps {
  isSignUp: boolean
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  selectedRole: UserRole | null
  loading: boolean
  emailValidation: { isValid: boolean; error?: string } | null
  onFirstNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onLastNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRoleChange: (role: 'landlord' | 'client') => void
  onSubmit: (e: React.FormEvent) => void
}

const AuthForm: React.FC<AuthFormProps> = ({
  isSignUp,
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
  selectedRole,
  loading,
  emailValidation,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onRoleChange,
  onSubmit,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Name fields for sign up */}
      {isSignUp && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-stone-700 mb-1.5">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={onFirstNameChange}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-stone-900 placeholder-stone-400 transition-all duration-200"
              placeholder="First name"
              required={isSignUp}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-stone-700 mb-1.5">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={onLastNameChange}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-stone-900 placeholder-stone-400 transition-all duration-200"
              placeholder="Last name"
              required={isSignUp}
              disabled={loading}
            />
          </div>
        </div>
      )}

      {/* Role selection for sign up */}
      {isSignUp && (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            I want to
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onRoleChange('landlord')}
              className={`relative p-4 border-2 rounded-xl text-center transition-all duration-300 group ${
                selectedRole === 'landlord'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-lg shadow-emerald-500/10'
                  : 'border-stone-200 hover:border-emerald-300 hover:bg-stone-50'
              }`}
              disabled={loading}
            >
              {/* Selection indicator */}
              {selectedRole === 'landlord' && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div className="text-2xl mb-2">🏠</div>
              <div className="font-semibold text-sm">Landlord</div>
              <div className="text-xs text-stone-500 mt-1">List properties</div>
            </button>
            <button
              type="button"
              onClick={() => onRoleChange('client')}
              className={`relative p-4 border-2 rounded-xl text-center transition-all duration-300 group ${
                selectedRole === 'client'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-lg shadow-emerald-500/10'
                  : 'border-stone-200 hover:border-emerald-300 hover:bg-stone-50'
              }`}
              disabled={loading}
            >
              {selectedRole === 'client' && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div className="text-2xl mb-2">🔍</div>
              <div className="font-semibold text-sm">Renter</div>
              <div className="text-xs text-stone-500 mt-1">Find a home</div>
            </button>
          </div>
        </div>
      )}

      {/* Email field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <input
            type="email"
            id="email"
            value={email}
            onChange={onEmailChange}
            className="w-full pl-12 pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-stone-900 placeholder-stone-400 transition-all duration-200"
            placeholder="you@example.com"
            required
            disabled={loading}
          />
        </div>
        {/* Email validation feedback */}
        {emailValidation && (
          <div className={`mt-1.5 text-xs flex items-center gap-1 ${emailValidation.isValid ? 'text-emerald-600' : 'text-red-600'}`}>
            {emailValidation.isValid ? (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Email looks good!</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{emailValidation.error}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Password field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-1.5">
          Password
        </label>
        <PasswordInput
          id="password"
          value={password}
          onChange={onPasswordChange}
          placeholder={isSignUp ? "Create a strong password" : "Enter your password"}
          required
          showValidation={isSignUp}
          disabled={loading}
        />
      </div>

      {/* Confirm password for sign up with match validation */}
      {isSignUp && (
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-stone-700 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            {/* Lock icon */}
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={onConfirmPasswordChange}
              className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 bg-white text-stone-900 placeholder-stone-400 ${
                confirmPassword.length === 0
                  ? 'border-stone-300 focus:ring-emerald-500'
                  : password === confirmPassword
                    ? 'border-emerald-400 focus:ring-emerald-500'
                    : 'border-red-400 focus:ring-red-500'
              }`}
              placeholder="Confirm your password"
              required={isSignUp}
              disabled={loading}
            />
            {/* Match indicator icon */}
            {confirmPassword.length > 0 && (
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                {password === confirmPassword ? (
                  <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            )}
          </div>
          {/* Password match feedback message */}
          {confirmPassword.length > 0 && (
            <div className={`mt-1.5 text-xs flex items-center gap-1 ${
              password === confirmPassword ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {password === confirmPassword ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Passwords match!</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>Passwords do not match</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-600 text-white py-3.5 px-4 rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>Processing...</span>
          </>
        ) : (
          <>
            <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </>
        )}
      </button>
    </form>
  )
}

export default AuthForm
